import { useTranslation } from "react-i18next";
import { FormEvent, useContext, useEffect, useRef } from "react";

import {
  Form,
  FormContext,
  FormErrorTypes,
  Modal,
  ModalContext,
  useBackendErrorMessage,
} from "dash-ui";

import {
  GetPropertyByIdQuery,
  GetUnitsDocument,
  useCreateUnitMutation,
  useEditUnitMutation,
} from "gql/generated.graphql";
import useValidation from "./hooks/useValidation";
import NameRow from "./components/NameRow/NameRow";
import BedroomBathroomRow from "./components/BedroomBathroomRow/BedroomBathroomRow";
import PriceRow from "./components/PriceRow/PriceRow";
import FloorPlan from "./components/FloorPlan/FloorPlan";
import { FormFields } from "./constants/constants";
import TypeRow from "./components/TypeRow/TypeRow";
import InteriorSqftRow from "./components/InteriorSqftRow/InteriorSqftRow";
import ExteriorSqftRow from "./components/ExteriorSqftRow/ExteriorSqftRow";
import { InventoryTypeEnum, unitSum } from "dash-common";

const AddPropertyInventoryModal = ({
  property,
}: {
  property: GetPropertyByIdQuery["getPropertyById"];
}) => {
  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation(["forms", "common", "pages"]);
  const translate = useBackendErrorMessage();
  const {
    formState,
    formOnSubmit,
    formOnError,
    resetForm,
    resetFormErrors,
    setFormValues,
  } = useContext(FormContext);
  const {
    isOpen,
    toggleModal,
    setPayload,
    payload: inventory,
  } = useContext(ModalContext);
  const valid = useValidation();
  const isEditMode = Boolean(inventory);

  const [createUnit, { loading: createLoading }] = useCreateUnitMutation({
    refetchQueries: [GetUnitsDocument],
  });
  const [editUnit, { loading: editLoading }] = useEditUnitMutation({
    refetchQueries: [GetUnitsDocument],
  });

  useEffect(() => {
    setFormValues({
      averageBathroomsNumber: {},
      averageBedroomsNumber: {},
      averageInteriorSqft: {},
      averageExteriorSqft: {},
      averageTotalSqft: {},
      inventoryType: InventoryTypeEnum.MODEL,
    });
  }, [isOpen]);

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    if (inventory) {
      setFormValues({
        ...inventory,
        prices: {
          min: inventory.startingPrice,
          max: inventory.highestPrice,
        },
        averageBathroomsNumber: {
          min: +inventory.averageBathroomsNumber?.min,
          max: inventory.averageBathroomsNumber?.max
            ? +inventory.averageBathroomsNumber?.max
            : null,
        },
        averageBedroomsNumber: {
          min: +inventory.averageBedroomsNumber?.min,
          max: +inventory.averageBedroomsNumber?.max
            ? +inventory.averageBedroomsNumber?.max
            : null,
        },
        floorPlanFile: inventory.floorPlanFile,
      });
    }

    setFormValues({
      prices: {
        min: 0,
        max: undefined,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventory]);

  const closeModal = () => {
    resetForm();
    resetFormErrors();
    toggleModal(false);
    setPayload(undefined);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();

    const { isLoading, errorSlug, ...formValues } = formState;

    if (
      !valid(FormFields.Name, formValues.name) ||
      !valid(FormFields.Prices, formValues.prices) ||
      !valid(FormFields.Bedrooms, formValues.averageBedroomsNumber) ||
      !valid(FormFields.Bathrooms, formValues.averageBathroomsNumber) ||
      !valid(FormFields.InteriorSqft, formValues.averageInteriorSqft) ||
      !valid(FormFields.ExteriorSqft, formValues.averageExteriorSqft)
    )
      return;

    const {
      inventoryType,
      name,
      averageBedroomsNumber,
      averageBathroomsNumber,
      averageInteriorSqft,
      averageExteriorSqft,
      prices,
      floorPlanFileId,
      customRooms,
    } = formValues;

    const input = {
      inventoryType,
      name,
      averageTotalSqft: {
        min: unitSum(averageInteriorSqft?.min, averageExteriorSqft?.min),
        max: unitSum(averageInteriorSqft?.max, averageExteriorSqft?.max),
      },
      averageInteriorSqft: {
        min: +averageInteriorSqft?.min,
        max:
          averageInteriorSqft?.max || averageInteriorSqft?.max === 0
            ? +averageInteriorSqft?.max
            : null,
      },
      averageExteriorSqft: {
        min: +averageExteriorSqft?.min,
        max:
          averageExteriorSqft?.max || averageExteriorSqft?.max === 0
            ? +averageExteriorSqft?.max
            : null,
      },
      startingPrice: prices?.min,
      highestPrice: prices?.max,
      customRooms,
      averageBedroomsNumber: {
        min: +averageBedroomsNumber?.min,
        max:
          averageBedroomsNumber?.max || averageBedroomsNumber?.max === 0
            ? +averageBedroomsNumber?.max
            : null,
      },
      averageBathroomsNumber: {
        min: +averageBathroomsNumber?.min,
        max:
          averageBathroomsNumber?.max || averageBathroomsNumber?.max === 0
            ? +averageBathroomsNumber?.max
            : null,
      },
      floorPlanFileId,
    };

    const onError = (error: { message: string }) => {
      formOnError(translate(error));
    };

    if (isEditMode) {
      return editUnit({
        variables: { input: { unitDetails: input, unitId: inventory?._id } },
        onCompleted: closeModal,
        onError,
      });
    }
    return createUnit({
      variables: { input: { ...input, propertyId: property._id } },
      onCompleted: closeModal,
      onError,
    });
  };

  const isLoading =
    createLoading || editLoading || formState[FormFields.FloorPlanLoading];

  return (
    <Modal
      md
      title={
        isEditMode
          ? t("pages:propertyPage.inventoryEditModalTitle") || ""
          : t("pages:propertyPage.inventoryCreateModalTitle") || ""
      }
      isOpen={isOpen}
      onRequestClose={closeModal}
      submitButton={{
        title: isEditMode ? t("common:labels.update") : t("common:labels.add"),
        isLoading,
        disabled: isLoading,
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{
        title: t("common:labels.cancel"),
        onClick: closeModal,
      }}
    >
      <Form ref={formRef} onSubmit={onSubmit}>
        <TypeRow />
        <NameRow />
        <BedroomBathroomRow />
        <InteriorSqftRow />
        <ExteriorSqftRow />
        <PriceRow />
        <FloorPlan />
        <Form.Group mt={3}>
          <Form.Feedback $errorType={FormErrorTypes.Invalid}>
            {formState.errorSlug}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
};

export default AddPropertyInventoryModal;
